import styled from "@emotion/styled";

import { COLORS, MEDIA_QUERIES } from "shared/config/constants";

import { BILLING_COLUMN_WIDTH } from "./billingDisplay";

export const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 28px 0 12px;
`;

export const SectionTitleRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
`;

export const SectionTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 400;
  color: ${COLORS.primary};
`;

export const NameCell = styled.span`
  display: flex;
  align-items: center;
  color: ${COLORS.textColorSecondary};
  line-height: 130%;
`;

export const SectionCount = styled.span<{ tone?: string }>`
  min-width: 22px;
  height: 20px;
  padding: 0 6px;
  border-radius: 2px;
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;
  text-align: center;
  color: ${COLORS.primary};
  background: ${({ tone }) => {
    if (tone === "onboarding") return "#FFE4CC";
    if (tone === "live") return "#D8F3EA";
    return "#E8E7EE";
  }};
`;

export const StaffBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: #e8e7ee;
  padding: 3px 6px;
  white-space: nowrap;
`;

export const InternalHint = styled.p`
  margin: 4px 0 0;
  font-size: 14px;
  font-weight: 300;
  color: ${COLORS.textColorSecondary};
  max-width: 720px;
  line-height: 1.45;
`;

/** Pan middle columns; Set live / Go live stays pinned next to the ⋯. */
export const BillingTableScroll = styled.div`
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  .ant-table-thead > tr > th {
    white-space: nowrap;
  }

  .ant-table-thead > tr > th.billing-action-cell,
  .ant-table-tbody > tr > td.billing-action-cell {
    position: sticky;
    right: 48px;
    z-index: 1;
    width: ${BILLING_COLUMN_WIDTH.action}px;
    min-width: ${BILLING_COLUMN_WIDTH.action}px;
    max-width: ${BILLING_COLUMN_WIDTH.action}px;
    padding-left: 8px;
    padding-right: 8px;
    overflow: hidden;
    text-align: right;
    background: #fff;
    box-shadow: -6px 0 10px -8px rgba(26, 24, 37, 0.1);
    ${MEDIA_QUERIES.w.gteMd} {
      right: 56px;
    }
  }

  .ant-table-thead > tr > th.billing-action-cell {
    z-index: 3;
    background: ${COLORS.table.alt.header.background};
  }

  .ant-table-tbody > tr > td.billing-action-cell > * {
    max-width: 100%;
    white-space: normal;
  }
`;

/** One bulk control — the header checkbox ▾ hid "Set live" in staging E2E. */
export const BulkSetLiveBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0 0 10px;
`;

export const BulkSetLiveHint = styled.span`
  font-size: 13px;
  color: ${COLORS.textColorSecondary};
`;

export const InlineHint = styled.span`
  font-size: 14px;
  color: ${COLORS.textColorSecondary};
  font-weight: 300;
  line-height: 1.45;
`;

export const BillingChip = styled.span<{ tone?: string }>`
  display: inline-flex;
  flex-direction: column;
  padding: 4px 10px;
  border-radius: 2px;
  font-size: 13px;
  line-height: 1.3;
  text-align: left;
  background: ${({ tone }) => {
    if (tone === "live" || tone === "scheduled") return "#D8F3EA";
    if (tone === "stopping") return "#FFE4CC";
    return "#E8E7EE";
  }};
  color: ${COLORS.primary};
`;

export const ChipSub = styled.span`
  font-size: 12px;
  color: ${COLORS.textColorSecondary};
`;

export const ReasonChip = styled.button<{ active?: boolean }>`
  height: 36px;
  padding: 0 14px;
  border: 1px solid ${({ active }) => (active ? COLORS.primary : "#DEDCE5")};
  background: ${({ active }) => (active ? "#F3F4F9" : "#fff")};
  border-radius: 2px;
  cursor: pointer;
  font-size: 14px;
  color: ${COLORS.primary};
  text-transform: none;
`;

export const ReasonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const ResetButton = styled.button`
  height: 36px;
  padding: 0 14px;
  border: 1px solid ${COLORS.primary};
  background: #fff;
  color: ${COLORS.primary};
  text-transform: uppercase;
  cursor: pointer;
  white-space: nowrap;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const SetLiveBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const PropertySummary = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px;
  border: 1px solid ${COLORS.borders};
  font-size: 14px;
`;

export const PropertySummaryName = styled.span`
  color: ${COLORS.primary};
`;

export const PropertySummaryUnits = styled.span`
  color: ${COLORS.textColorSecondary};
  font-size: 12px;
  white-space: nowrap;
`;

export const DateField = styled.div`
  display: flex;
  flex-direction: column;
`;

export const DateFieldLabel = styled.span`
  display: block;
  font-size: 14px;
  color: ${COLORS.textColorSecondary};
  margin-bottom: 6px;
`;

export const DateActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: nowrap;
`;

export const DatePickerWrap = styled.div`
  width: 168px;
  flex: none;

  .MuiFormControl-root,
  .MuiTextField-root {
    width: 168px;
    min-width: 0;
  }

  .MuiInputLabel-root {
    display: none;
  }

  .MuiOutlinedInput-root {
    height: 40px;
  }
`;

export const DateShortcuts = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: none;
`;

export const ImmediateWrap = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  flex: none;
`;

export const ImmediatePill = styled.button<{ active?: boolean }>`
  display: inline-flex;
  align-items: center;
  height: 32px;
  padding: 0 12px;
  border: 1px solid ${({ active }) => (active ? "#03AB70" : "#DEDCE5")};
  border-radius: 16px;
  background: ${({ active }) => (active ? "#E6F7F0" : "#fff")};
  color: ${({ active }) => (active ? "#02754D" : COLORS.primary)};
  font-size: 14px;
  font-weight: ${({ active }) => (active ? 500 : 400)};
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  text-transform: none;
`;

export const ImmediateTip = styled.span`
  position: absolute;
  left: 0;
  top: calc(100% + 8px);
  width: 248px;
  padding: 10px 12px;
  background: #fff;
  color: ${COLORS.primary};
  font-size: 13px;
  font-weight: 400;
  line-height: 1.45;
  border: 1px solid ${COLORS.borders};
  box-shadow: 0 4px 12px #78739526;
  z-index: 60;
`;

export const RestartHint = styled.div`
  font-size: 12px;
  color: ${COLORS.textColorSecondary};
  margin-top: 8px;
`;

export const ImpactTable = styled.div`
  border: 1px solid ${COLORS.borders};
`;

export const ImpactRow = styled.div`
  display: grid;
  grid-template-columns: 96px minmax(0, 1fr);
  gap: 4px 16px;
  padding: 10px 16px;
  font-size: 13px;

  & + & {
    border-top: 1px solid #f2f2f5;
  }
`;

export const ImpactLabel = styled.span`
  color: ${COLORS.textColorSecondary};
  font-size: 12px;
`;

export const ImpactCopy = styled.div`
  display: flex;
  flex-direction: column;
`;

export const ImpactTitle = styled.strong`
  color: ${COLORS.primary};
  font-weight: 600;
`;

export const ImpactHint = styled.span`
  display: block;
  color: ${COLORS.textColorSecondary};
  font-size: 12px;
`;

export const WarningNote = styled.p`
  margin: 12px 0 0;
  padding: 10px 12px;
  border-radius: 8px;
  background: #ffede5;
  color: ${COLORS.primary};
  font-size: 13px;
`;

export const HistoryScroll = styled.div`
  max-height: min(360px, 50vh);
  overflow-y: auto;
  margin: 0 -8px;
  padding: 0 8px;
`;

export const HistoryPager = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
`;

export const HistoryList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

export const HistoryItem = styled.li`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 0;
  border-bottom: 1px solid ${COLORS.borders};

  &:last-child {
    border-bottom: 0;
  }
`;

export const HistoryTitle = styled.strong`
  color: ${COLORS.primary};
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
`;

export const HistoryMeta = styled.div`
  color: ${COLORS.textColorSecondary};
  font-size: 13px;
  line-height: 18px;
  overflow-wrap: anywhere;
`;

export const HistoryDetail = styled.div`
  color: ${COLORS.primary};
  font-size: 13px;
  line-height: 18px;
`;

export const HistoryReason = styled.div`
  color: ${COLORS.textColorSecondary};
  font-size: 13px;
  line-height: 18px;
  overflow-wrap: anywhere;
`;
